import { Link } from 'react-router-dom';
import { api, type Dashboard } from '../lib/api';
import { useAsync } from '../lib/hooks';
import { fmtDate, fmtNumber, fmtRelative, shortFp } from '../lib/format';
import { ErrorAlert, LastSeen, LicenseStatusBadge, Loading, PageHeader, TypeBadge } from '../components/ui';
import { useT } from '../i18n';

export function DashboardPage() {
  const t = useT();
  const { data, error, loading } = useAsync(() => api.get<Dashboard>('/v1/admin/dashboard'), []);
  if (loading && !data) return <Loading />;
  return (
    <>
      <PageHeader title={t('dashboard.title')} subtitle={t('dashboard.subtitle')} />
      <ErrorAlert error={error} />
      {data && (
        <>
          <div className="grid grid-4">
            <Kpi label={t('dashboard.kpiActive')} value={data.licenses_active} to="/licencias?status=active" />
            <Kpi label={t('dashboard.kpiMachines')} value={data.activations_active} to="/maquinas" />
            <Kpi label={t('dashboard.kpiFloating')} value={data.floating_sessions_active} />
            <Kpi
              label={t('dashboard.kpiExpiring')}
              value={data.licenses_expiring_30d}
              to="/licencias?expiring_within_days=30"
              tone={data.licenses_expiring_30d > 0 ? 'warning' : undefined}
            />
          </div>

          <div className="grid grid-2" style={{ marginTop: 16 }}>
            <section className="card">
              <div className="card-header">
                <h2>{t('dashboard.recent')}</h2>
                <Link to="/maquinas">{t('dashboard.seeAll')}</Link>
              </div>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>{t('dashboard.machine')}</th>
                      <th>{t('dashboard.license')}</th>
                      <th>{t('dashboard.when')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recent_activations.length === 0 && (
                      <tr>
                        <td colSpan={3} className="empty">{t('dashboard.noActivations')}</td>
                      </tr>
                    )}
                    {data.recent_activations.map((a) => (
                      <tr key={a.id}>
                        <td>
                          <div>{a.machine_name ?? <span className="muted">{shortFp(a.fingerprint)}</span>}</div>
                          <div className="muted" style={{ fontSize: 12 }}>
                            {a.os ?? ''} {a.app_version ? `· v${a.app_version}` : ''}
                          </div>
                        </td>
                        <td>
                          <Link to={`/licencias/${a.license_id}`} className="key">
                            {a.license_key}
                          </Link>
                          <div className="muted" style={{ fontSize: 12 }}>{a.product_code}</div>
                        </td>
                        <td title={a.activated_at}>{fmtRelative(a.activated_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="card">
              <div className="card-header">
                <h2>{t('dashboard.expiring')}</h2>
                <Link to="/licencias?expiring_within_days=30&sort=expires_at">{t('dashboard.seeAll')}</Link>
              </div>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>{t('dashboard.license')}</th>
                      <th>{t('common.customer')}</th>
                      <th>{t('dashboard.expires')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.expiring_soon.length === 0 && (
                      <tr>
                        <td colSpan={3} className="empty">{t('dashboard.noExpiring')}</td>
                      </tr>
                    )}
                    {data.expiring_soon.map((l) => (
                      <tr key={l.id}>
                        <td>
                          <Link to={`/licencias/${l.id}`} className="key">
                            {l.key}
                          </Link>
                          <div>
                            <TypeBadge type={l.type} mode={l.mode} />
                          </div>
                        </td>
                        <td>{l.customer_name ?? <span className="muted">—</span>}</td>
                        <td>
                          <div>{fmtDate(l.expires_at)}</div>
                          <LicenseStatusBadge status={l.status} expiresAt={l.expires_at} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </div>

          <div className="grid grid-2" style={{ marginTop: 16 }}>
            {data.inactive_30d && (
              <section className="card">
                <div className="card-header">
                  <h2>{t('dashboard.inactive', { n: data.licenses_inactive_30d ?? data.inactive_30d.length })}</h2>
                  <Link to="/licencias?status=active&inactive_days=30&sort=last_seen_at">{t('dashboard.seeAll')}</Link>
                </div>
                <p className="muted" style={{ marginTop: 0 }}>{t('dashboard.inactiveHelp')}</p>
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>{t('dashboard.license')}</th>
                        <th>{t('common.customer')}</th>
                        <th>{t('dashboard.lastSeen')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.inactive_30d.length === 0 && (
                        <tr>
                          <td colSpan={3} className="empty">{t('dashboard.noInactive')}</td>
                        </tr>
                      )}
                      {data.inactive_30d.map((l) => (
                        <tr key={l.id}>
                          <td>
                            <Link to={`/licencias/${l.id}`} className="key">
                              {l.key}
                            </Link>
                            <div className="muted" style={{ fontSize: 12 }}>{l.product_name}</div>
                          </td>
                          <td>{l.customer_name ?? <span className="muted">—</span>}</td>
                          <td><LastSeen at={l.last_seen_at} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}
            <section className="card">
              <h2>{t('dashboard.byProduct')}</h2>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>{t('common.product')}</th>
                      <th>{t('dashboard.code')}</th>
                      <th className="num">{t('dashboard.active')}</th>
                      <th className="num">{t('dashboard.total')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.by_product.map((p) => (
                      <tr key={p.product_id}>
                        <td>
                          <Link to={`/productos/${p.product_id}`}>{p.name}</Link>
                        </td>
                        <td>
                          <code>{p.code}</code>
                        </td>
                        <td className="num">{p.active}</td>
                        <td className="num">
                          <Link to={`/licencias?product_id=${p.product_id}`}>{p.total}</Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </div>
        </>
      )}
    </>
  );
}

function Kpi({ label, value, to, tone }: { label: string; value: number; to?: string; tone?: 'warning' }) {
  const body = (
    <>
      <div className="label">{label}</div>
      <div className="value" style={tone === 'warning' ? { color: 'var(--warning)' } : undefined}>
        {fmtNumber(value)}
      </div>
    </>
  );
  return (
    <div className="card kpi">
      {to ? (
        <Link to={to} style={{ color: 'inherit', textDecoration: 'none' }}>
          {body}
        </Link>
      ) : (
        body
      )}
    </div>
  );
}
